/**
 * Action Register + Verification Log config: the ONE source of the Phase 13 vocabulary.
 *
 * 13-04 (verbs), 13-05 (authority + lenses), 13-06 (outbox) and 13-07 (Graph I/O) run in
 * parallel and all import from here, so there is one copy of every list id, field name and
 * choice set rather than four drifting ones.
 *
 * Sources:
 *   - 09.1-CONTEXT §3 (Action Register columns), §4 (Verification Log columns), §5 (audit
 *     siblings), §8a (the provisioned list GUIDs), §8b (`Executor` / `ReleasedAt` / `ReleaseNote`);
 *   - 13-DECISIONS D-13-00-1 (`Retired` + `ReleasedBy`), D-13-00-2 (`DecisionClass` + the
 *     fail-safe regulatory classifier inputs), ISS-116 (the Verification Log append-only rule);
 *   - build/action-register/provision_action_register.py (the choice sets as provisioned) and
 *     extend_action_register_v13.py (13-01: `ReleasedBy` is Text holding a UPN, `DecisionClass`
 *     has NO default, `Retired` is appended last to `Status`).
 *
 * Lists are addressed BY GUID, never by `LIST_NAMES` title resolution: a title that fails to
 * resolve becomes '' in `useGraphClient`, which would point a write at nothing.
 *
 * Field values are SharePoint INTERNAL names. Every 09.1 column is space-free PascalCase, so no
 * `x0020()` encoding is needed. Person columns (`Owner`, `VerifiedBy`) are read back by Graph as
 * `<Name>LookupId` and written as `"<Name>LookupId": "<site user id>"` (RESEARCH Pitfall 4); use
 * `lookupIdField()` rather than composing that suffix inline.
 *
 * The audit siblings use the shared contract `AUDIT_FIELDS` in `registers.ts`; it is reused, not
 * redeclared here.
 */

// ---------------------------------------------------------------------------
// Lists (09.1-CONTEXT §8a, provisioned 2026-09-08, read-back verified)
// ---------------------------------------------------------------------------

/** The four Phase 9.1 list GUIDs on /sites/QualityandSafety. Address them by GUID only. */
export const AR_LISTS = {
  /** `Action Register` (parent). */
  actionRegister: '4c775588-fc37-4983-8b9f-0c705710825e',
  /** `Action Register Audit Log` (glass-box sibling). */
  actionRegisterAudit: 'ac9bd70a-6c70-48bb-ad7f-08a19e93d40a',
  /** `Verification Log` (parent, append-only by convention, ISS-116). */
  verificationLog: '60429e3d-3800-414a-947d-8527f087bd9c',
  /** `Verification Log Audit Log` (glass-box sibling). */
  verificationLogAudit: 'd2662fec-3474-40f0-94b6-4bd1a18a024e',
} as const;

/** Human reference prefixes, composed `<PREFIX>-<Id>` from the SP item id (ISS-008). */
export const AR_PREFIX = 'ACT' as const;
export const VL_PREFIX = 'VER' as const;

/** `ACT-<id>` — the `ParentRef` an Action Register audit row carries. */
export function actionRef(id: number | string): string {
  return `${AR_PREFIX}-${id}`;
}

/** `VER-<id>` — the `ParentRef` a Verification Log audit row carries. */
export function verificationRef(id: number | string): string {
  return `${VL_PREFIX}-${id}`;
}

/**
 * Graph reads a Person column back as `<Name>LookupId` (a string site-user id) and writes it the
 * same way. `lookupIdField('Owner')` → `'OwnerLookupId'`.
 */
export function lookupIdField<N extends string>(name: N): `${N}LookupId` {
  return `${name}LookupId`;
}

// ---------------------------------------------------------------------------
// Fields (SP internal names)
// ---------------------------------------------------------------------------

/**
 * Action Register parent fields: every 09.1 §3 column, the 09.1 §8b additions (`Executor`,
 * `ReleasedAt`, `ReleaseNote`) and the 13-01 additions (`ReleasedBy`, `DecisionClass`).
 * Built-ins `Created` / `Author` are not listed; read them from the list item envelope.
 */
export const AR_FIELDS = {
  title: 'Title',
  kind: 'Kind',
  source: 'Source',
  sourceRef: 'SourceRef',
  parentType: 'ParentType',
  parentId: 'ParentId',
  actionType: 'ActionType',
  description: 'Description',
  options: 'Options',
  decidedOption: 'DecidedOption',
  ownerRole: 'OwnerRole',
  /** Person. Read/write as `OwnerLookupId`. */
  owner: 'Owner',
  status: 'Status',
  tier: 'Tier',
  dueDate: 'DueDate',
  verificationMethod: 'VerificationMethod',
  evidence: 'Evidence',
  acceptedAt: 'AcceptedAt',
  completedAt: 'CompletedAt',
  /** Person. Read/write as `VerifiedByLookupId`. */
  verifiedBy: 'VerifiedBy',
  verifiedAt: 'VerifiedAt',
  verdict: 'Verdict',
  escalation: 'Escalation',
  createdByAgent: 'CreatedByAgent',
  cardId: 'CardId',
  // 09.1 §8b (2026-09-09)
  executor: 'Executor',
  releasedAt: 'ReleasedAt',
  releaseNote: 'ReleaseNote',
  // 13-01 (D-13-00-1 / D-13-00-2)
  /** Text, holding the releaser's UPN (not Person: D-13-00-4 / D-13-01-1). */
  releasedBy: 'ReleasedBy',
  /** Choice, no default: blank = unclassified, treated fail-safe as Regulatory (D-13-00-2). */
  decisionClass: 'DecisionClass',
} as const;

/** The Action Register Person columns, as their Graph read/write names. */
export const AR_LOOKUP_FIELDS = {
  owner: lookupIdField(AR_FIELDS.owner),
  verifiedBy: lookupIdField(AR_FIELDS.verifiedBy),
} as const;

/** Verification Log parent fields: the 09.1 §4 columns, in order. */
export const VL_FIELDS = {
  title: 'Title',
  cardId: 'CardId',
  idempotencyKey: 'IdempotencyKey',
  step: 'Step',
  targetSystem: 'TargetSystem',
  recordRef: 'RecordRef',
  action: 'Action',
  payloadHash: 'PayloadHash',
  agentIdentity: 'AgentIdentity',
  writtenAt: 'WrittenAt',
  verifierRole: 'VerifierRole',
  /** Person. Read/write as `VerifiedByLookupId`. */
  verifiedBy: 'VerifiedBy',
  verifiedAt: 'VerifiedAt',
  verdict: 'Verdict',
  exceptionReason: 'ExceptionReason',
} as const;

/** The Verification Log Person column, as its Graph read/write name. */
export const VL_LOOKUP_FIELDS = {
  verifiedBy: lookupIdField(VL_FIELDS.verifiedBy),
} as const;

/**
 * The ONLY Verification Log fields that may change after a row is created (append-only rule,
 * 09.1 §4 / ISS-116). Each change writes a `StateChange` row to the VL audit sibling.
 * Anything else in a VL patch is a bug.
 */
export const VL_MUTABLE_FIELDS = [
  VL_FIELDS.verifierRole,
  VL_FIELDS.verifiedBy,
  VL_FIELDS.verifiedAt,
  VL_FIELDS.verdict,
  VL_FIELDS.exceptionReason,
] as const;

// ---------------------------------------------------------------------------
// Vocabularies (choice sets, in provisioned order)
// ---------------------------------------------------------------------------

/** `Status` — the seven of 09.1 §3, plus `Retired` appended by 13-01 (D-13-00-1). */
export const AR_STATUS = [
  'Proposed',
  'Accepted',
  'InProgress',
  'Done',
  'Verified',
  'Rejected',
  'NotMine',
  'Retired',
] as const;

/**
 * The open statuses. Mirrors the poller's `OPEN_ACT` (n8n `qzeLsseyJXjU6oD9`,
 * `Compute_Escalations` / `Resolve_New_Items`): only these rows are carded, escalated or held in
 * Oversight's release queue. `Retired`, `NotMine`, `Done`, `Verified` and `Rejected` are not open.
 */
export const OPEN_STATUSES = ['Proposed', 'Accepted', 'InProgress'] as const;

/** `Kind` (09.1 §2). */
export const AR_KIND = ['Action', 'Approval', 'Decision'] as const;

/** `Source` — the origin surface. */
export const AR_SOURCE = [
  'Incident',
  'Complaint',
  'CI',
  'Feedback',
  'Risk',
  'Audit',
  'Meeting',
  'StatementException',
  'Other',
] as const;

/** `ParentType` — the closure-trail key (the card's name). */
export const AR_PARENT_TYPE = [
  'Incident',
  'Complaint',
  'CI',
  'Feedback',
  'Risk',
  'Audit',
  'Meeting',
  'StatementRun',
  'Other',
] as const;

/**
 * `OwnerRole` (Action Register) and `VerifierRole` (Verification Log) — the same nine. The first
 * four are QMS App Roles; `Admin` / `Board` / `QCAC` / `Wellness` / `DAG` have no App Role and fall
 * to Oversight's lens (D-13-00-5).
 */
export const OWNER_ROLES = [
  'Oversight',
  'CareManager',
  'HRWHS',
  'Finance',
  'Admin',
  'Board',
  'QCAC',
  'Wellness',
  'DAG',
] as const;

/** `Tier` (JOB_CARD_TEMPLATE tiers). */
export const AR_TIER = ['write-then-verify', 'verify-then-write', 'human-only'] as const;

/** `VerificationMethod`. */
export const VERIFICATION_METHOD = ['self_attestation', 'evidence', 'manager', 'sampled'] as const;

/** Action Register `Verdict`. */
export const VERDICT = ['pending', 'verified', 'rejected', 'sampled-ok'] as const;

/** Verification Log `Verdict`: the Action Register four plus `packet-delivered` (card §12). */
export const VL_VERDICT = ['pending', 'verified', 'rejected', 'sampled-ok', 'packet-delivered'] as const;

/** `Escalation` — the highest level reached. Never downgraded. */
export const ESCALATION = ['none', 'manager', 'brief'] as const;

/** `Executor` (09.1 §8b). */
export const AR_EXECUTOR = ['Human', 'Agent'] as const;

/** `DecisionClass` (D-13-00-2). Blank means unclassified, never "Ordinary". */
export const DECISION_CLASS = ['Ordinary', 'Regulatory'] as const;

/** Verification Log `TargetSystem`. */
export const VL_TARGET_SYSTEM = [
  'QMS',
  'ShiftCare',
  'SharePoint',
  'Teams',
  'Xero',
  'Email',
  'Other',
] as const;

// ---------------------------------------------------------------------------
// Regulatory classifier inputs (D-13-00-2)
// ---------------------------------------------------------------------------

/**
 * Inputs to the fail-safe regulatory classifier (13-05 `isRegulatoryDecision`). An unclassified
 * Decision is Regulatory when it comes from an Incident (`Source` or `ParentType`), when its
 * `ActionType` is in `actionTypes`, or when `Title`, `Options` or `ActionType` match `pattern`.
 * The UI never guesses "Ordinary" when unsure.
 *
 * Mirrored in Python by build/action-register/extend_action_register_v13.py (the 13-01 back-fill);
 * the two must move together. `pattern` has no `g` flag, so `.test()` is stateless.
 */
export const REGULATORY = {
  actionTypes: ['reportability-decision', 'serious-injury-determination', 'reopen-or-void'],
  pattern: /SIRS|reportab|restrictive|Commission|WorkSafe/i,
} as const;

// ---------------------------------------------------------------------------
// Verbs and timings
// ---------------------------------------------------------------------------

/**
 * The Action Register verbs (13-04 builds each as `(row, actor, input) → VerbResult`). These are
 * a separate authority from `RegisterAction` in roles.ts, whose floor stays untouched (D-13-00-5).
 */
export const ACTION_VERBS = [
  'accept',
  'done',
  'notMine',
  'verify',
  'reject',
  'decide',
  'release',
  'retire',
  'reopen',
  // ISS-186 / D-13-R-1 (Andy, 4 Oct 2026): Oversight hands a NotMine row to a new owner role.
  'reassign',
] as const;

/** The one-tap undo window: the toast duration, after which the outbox commits (13-06). */
export const UNDO_WINDOW_MS = 6000;

/** Requests per sequential Graph `$batch` chunk in the release sitting (Graph's limit is 20). */
export const RELEASE_BATCH_SIZE = 20;
